import { Box, styled } from '@mui/material';
import { Edit } from '@styled-icons/boxicons-solid/Edit';
import ThemeButton from 'DesignSystem/Buttons/ThemeButton/ThemeButton';
import { ImpactLevelCategories } from 'FeatureModules/ImpactClaim/utils/impactLevelCategories';
import { ImpactClaim } from 'FeatureModules/Request/types/impactClaim.types';
import useDialog from 'hooks/useDialog';
import React, { FC, Fragment } from 'react';
import ClaimTitleAndStatus from '../ClaimTitleAndStatus';
import LevelPill from '../LevelPill';
import { convertHexToRGBarray } from 'utils/color.utils';
import { Booleanish, Tbooleanish } from 'types/booleanish.types';

interface IClaimHeaderProps {
  impactClaim: ImpactClaim;
  loading: boolean;
}

export const HeaderContainer = styled(Box)<{ invalid: Tbooleanish; pending: Tbooleanish }>(
  ({ theme, invalid, pending }) => ({
    backgroundColor:
      invalid === 'true' ? `rgba(${convertHexToRGBarray(theme.palette.error.main)}, 0.15)` : '#fff',
    padding: theme.spacing(1.5),
    borderRadius: '6px 6px 0 0',
    position: 'relative',
    overflow: 'hidden',

    '&:before': {
      content: '""',
      display: 'block',
      width: '100%',
      height: '100%',
      position: 'absolute',
      left: 0,
      top: 0,
      borderColor: pending === 'true' ? '#d5daee' : '#F4F5FA',
      borderRadius: '6px 6px 0 0',
      borderWidth: invalid === 'true' ? '0' : '1px',
      borderStyle: pending === 'true' ? 'dashed' : 'solid',
    },
  })
);

export const Description = styled('div')(({ theme }) => ({
  background: theme.palette.grey[200],
  padding: theme.spacing(2),
  borderRadius: '0 0 6px 6px',
}));

const ClaimHeader: FC<IClaimHeaderProps> = ({ impactClaim, loading }) => {
  const { openDialog } = useDialog();

  const handleClickEdit = () =>
    openDialog({
      type: 'EDIT_IMPACT_CLAIM',
      props: {
        claim: impactClaim,
      },
    });

  const impactLevel = ImpactLevelCategories[impactClaim?.impactLevel];

  return (
    <Fragment>
      <HeaderContainer
        pending={Booleanish(impactClaim?.isPending)}
        invalid={Booleanish(impactClaim?.isInvalid)}
        display="flex"
        justifyContent="space-between"
      >
        <Box display="flex" alignItems="start" gap={2.5}>
          <ClaimTitleAndStatus
            impactCatalogItem={impactClaim?.impactCatalogItem}
            loading={loading}
            claimStatus={impactClaim?.status || 'CREATED'}
            enlargeTitle
          />

          <LevelPill claimLevel={impactLevel} title={impactClaim?.impactLevelTitle} />
        </Box>
        <Box display="flex" alignItems="center">
          {/* TODO: Add unarchive functionality */}
          {/* {impactClaim.isArchived && (
          <Button onClick={unarchive} data-testid="unarchive-claim-button">Unarchive</Button>
        ) : ( */}
          {impactClaim && (
            <ThemeButton
              onClick={handleClickEdit}
              color={impactClaim?.isInvalid ? 'YELLOW' : 'BLUE_ICE'}
              size="medium"
              data-testid="edit-claim-button"
              startIcon={<Edit size={16} />}
            >
              Edit
            </ThemeButton>
          )}
          {/* ) */}
        </Box>
      </HeaderContainer>
      <Description>{impactClaim?.impactCatalogItem?.definition}</Description>
    </Fragment>
  );
};

export default ClaimHeader;
